<template>
  <div
    class="custom-tab"
    :class="[{ 'is-without-control': !control }]"
  >
    <div class="custom-tab__in">
      <div class="custom-tab__navigation">
        <ul
          class="custom-tab__nav"
          @mousedown="startDrag"
          @mousemove="onDrag"
          @mouseup="stopDrag"
          @mouseleave="stopDrag"
          @touchstart="startDrag"
          @touchmove="onDrag"
          @touchend="stopDrag"
        >
          <li
            v-for="(tab, index) in regularTabs"
            :id="tab.id"
            :key="index"
            :class="[
              'custom-tab__item',
              { 'is-active': currentTab === tab.id },
              { 'is-hidden': tab.hideButton },
              `custom-tab__item_${tab.id}`
            ]"
          >
            <button
              v-if="!tab.hideButton"
              :id="`button-${tab.id}`"
              class="custom-tab__button"
              type="button"
              :tabindex="tabIndex"
              :disabled="tab.disabled"
              @mouseup="onTabClick(tab.id)"
            >
              <slot
                :name="currentTabButton(tab)"
                :currentTab="tab"
                :index="index"
              >
                {{ tab.label }}
              </slot>

              <Count
                v-if="tab?.countOptions"
                :number="metrics?.[tab?.countOptions.number] ?? 0"
                :notify="metrics?.[tab?.countOptions.notify] ?? 0"
              />
            </button>
          </li>
        </ul>
        <div
          v-if="actionTabs.length"
          class="custom-tab__actions"
        >
          <button
            v-for="(tab, index) in actionTabs"
            :id="`button-${tab.id}`"
            :key="tab.id"
            :class="[
              'custom-tab__action',
              { 'is-active': currentTab === tab.id },
              { 'is-hidden': tab.hideButton },
              `custom-tab__action_${tab.id}`
            ]"
            :tabindex="tabIndex"
            :disabled="tab.disabled"
            type="button"
            @click="onTabClick(tab.id)"
          >
            <slot
              :name="currentTabButton(tab)"
              :currentTab="tab"
              :index="index"
            >
              {{ tab.label }}
            </slot>
          </button>
        </div>
        <slot name="after-navigation" />

        <div
          v-if="control"
          class="custom-tab__control"
        >
          <button
            type="button"
            class="custom-tab__control-item"
            :disabled="disabledLeftButton"
            :tabindex="tabIndex"
            @click="onTabLeft"
          >
            <icon
              data="@icon/arrow-long.svg"
              dir="down"
              :fill="true"
              color="#7D92A7"
            />
          </button>

          <button
            type="button"
            class="custom-tab__control-item"
            :disabled="disabledRightButton"
            :tabindex="tabIndex"
            @click="onTabRight"
          >
            <icon
              data="@icon/arrow-long.svg"
              :fill="true"
              color="#7D92A7"
            />
          </button>

          <slot name="after-control" />
        </div>
      </div>
      <div class="custom-tab__content">
        <slot
          name="tabContent"
          :currentTab="tabs[getActiveTabIndex]"
          :index="getActiveTabIndex"
        >
          <template v-for="(tab, index) in tabs">
            <section
              v-if="currentTab === tab.id"
              :key="index"
              class="custom-tab__container"
              :class="[
                currentTabContent(tab),
                { 'is-expanded': expandedTab && tab.id === 'comments' }
              ]"
            >
              <slot
                :name="currentTabContent(tab)"
                :currentTab="tab"
                :index="index"
              >
                {{ tab.label }}
              </slot>
            </section>
          </template>
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"

import Count from "@/components/Count/index.vue"

import { BlockTab } from "@/models"

defineOptions({
  name: "Tabs"
})

const props = withDefaults(
  defineProps<{
    tabs?: BlockTab[]
    initTabIndex?: number
    removeFocus?: boolean
    control?: boolean
    changeCurrentTab?: string
    metrics?: { [key: string]: number }
    disabled?: boolean
    expandedTab?: boolean
  }>(),
  {
    tabs: () => [],
    removeFocus: false,
    control: true,
    changeCurrentTab: "",
    metrics: () => ({}),
    disabled: false,
    expandedTab: false
  }
)

const emit = defineEmits<{
  (event: "onTabClick", tabId?: string): void
  (
    event: "tab-change",
    payload: { currentTab: BlockTab | undefined; index: number; prev: string }
  ): void
}>()

const disabledLeftButton = ref(true)
const disabledRightButton = ref(false)
const currentTab = ref("")
const prevTab = ref("")

const isDragging = ref(false)
const isMouseDown = ref(false)
const startX = ref(0)
const scrollLeft = ref(0)

const regularTabs = computed<BlockTab[]>(() => props.tabs.filter(tab => !tab.isLast))
const actionTabs = computed<BlockTab[]>(() => props.tabs.filter(tab => tab.isLast))
const getActiveTabIndex = computed(() => props.tabs.findIndex(tab => tab.id === currentTab.value))
const updateTabId = computed(() => {
  if (props.initTabIndex === undefined) return ""
  return props.tabs[props.initTabIndex]?.id
})
const tabIndex = computed(() => (props.removeFocus ? -1 : 0))

function onUpdateCurrentTab(newCurrentTab?: string) {
  if (!newCurrentTab) return

  currentTab.value = newCurrentTab
  handleTabChange(getActiveTabIndex.value)
  setTimeout(() => handleButtonClick("center"), 0)
}

function onTabClick(tabId: string) {
  if (isDragging.value || props.disabled) return
  const selectedTab = props.tabs.find(tab => tab.id === tabId)
  if (!selectedTab || selectedTab.disabledAction || selectedTab.disabled) return

  emit("onTabClick", tabId)
  prevTab.value = currentTab.value
  onUpdateCurrentTab(tabId)
  handleButtonClick("center")
}

function handleButtonClick(_inline?: string) {
  const el = document.getElementById(currentTab.value)
  if (el) el.scrollIntoView({ inline: "nearest", behavior: "smooth", block: "center" })
}

function currentTabButton(tab: BlockTab): string {
  return "button-" + tab.id
}

function currentTabContent(tab: BlockTab): string {
  return "tab-" + tab.id
}

function onTabLeft() {
  const nextTab = props.tabs[getActiveTabIndex.value - 1]
  if (!nextTab) return

  onUpdateCurrentTab(nextTab.id)
  handleButtonClick("end")
  emit("onTabClick")
}

function onTabRight() {
  const nextTab = props.tabs[getActiveTabIndex.value + 1]
  if (!nextTab) return

  onUpdateCurrentTab(nextTab.id)
  handleButtonClick("center")
  emit("onTabClick")
}

function onDisableLeftButton(index: number) {
  disabledLeftButton.value = index === 0
}

function onDisableRightButton(index: number) {
  disabledRightButton.value = index === props.tabs.length - 1
}

function handleTabChange(tabIndex: number) {
  onDisableLeftButton(tabIndex)
  onDisableRightButton(tabIndex)

  emit("tab-change", {
    index: tabIndex,
    currentTab: props.tabs[tabIndex],
    prev: prevTab.value
  })
}

function startDrag(event: MouseEvent | TouchEvent) {
  isMouseDown.value = true
  const nav = event.currentTarget as HTMLElement
  startX.value =
    (event instanceof MouseEvent ? event.pageX : event.touches[0].pageX) - nav.offsetLeft
  scrollLeft.value = nav.scrollLeft
}

function onDrag(event: MouseEvent | TouchEvent) {
  if (!isMouseDown.value) return

  event.preventDefault()
  const nav = event.currentTarget as HTMLElement
  const x = (event instanceof MouseEvent ? event.pageX : event.touches[0].pageX) - nav.offsetLeft
  if (x === startX.value) return

  const walk = (x - startX.value) * 1.5
  nav.scrollLeft = scrollLeft.value - walk
  isDragging.value = true
}

function stopDrag() {
  isDragging.value = false
  isMouseDown.value = false
}

watch(updateTabId, tabId => {
  onUpdateCurrentTab(tabId)
  setTimeout(() => handleButtonClick("center"), 0)
})

watch(
  () => props.changeCurrentTab,
  tabId => {
    if (tabId === currentTab.value) return
    onUpdateCurrentTab(tabId)
  }
)

if (props.changeCurrentTab) {
  onUpdateCurrentTab(props.changeCurrentTab)
} else {
  onUpdateCurrentTab(props.tabs[props.initTabIndex ?? 0]?.id)
}
</script>
